import type { Actor } from './types/actor.js';
import type { ModuleManifest } from './types/module.js';
import type { ModuleKey } from './types/tenant.js';
import { can, isStaffSurface, personaOf } from './derived/permissions.js';

export interface NavSubItem {
  key: string;
  label: string;
  path: string;
  /** Module the sub-item needs. */
  requires?: ModuleKey;
  /** Only on the staff surface, never under impersonation or for a client. */
  staffOnly?: boolean;
}

export interface NavGroup {
  key: string;
  label: string;
  /** Where clicking the group row lands. */
  path: string;
  /** The group shows when the tenant has any of these. Absent means always. */
  requiresAny?: ModuleKey[];
  items: NavSubItem[];
}

/** Everything routing and navigation need, built once from the module manifests. */
export interface NavModel {
  groups: NavGroup[];
  routePrefixes: Array<{ prefix: string; module: ModuleKey }>;
}

export const ADMIN_ROUTE_PREFIX = '/admin';
export const ORG_ROUTE = '/organization';
export const HOME_ROUTE = '/';

/** Spec 00: Administration is always the last group. */
export const ADMIN_GROUP: NavGroup = {
  key: 'admin',
  label: 'Administration',
  path: '/admin/tenants',
  items: [
    { key: 'tenants', label: 'Tenants', path: '/admin/tenants', staffOnly: true },
    { key: 'staff', label: 'DinoCloud team', path: '/admin/staff', staffOnly: true },
    { key: 'roles', label: 'Roles and permissions', path: '/admin/roles', staffOnly: true },
    { key: 'org', label: 'Client-side view', path: ORG_ROUTE },
  ],
};

/**
 * Builds the nav from manifests. Groups are ordered by `navGroup.order`; modules in the
 * same group merge their items in manifest order. Unshipped modules are left out entirely:
 * nothing a client could see refers to a module that does not exist yet. Staff still reach
 * their in-development screens through the module's base path.
 */
export function buildNavModel(manifests: readonly ModuleManifest[], opts: { includeUnshipped?: boolean } = {}): NavModel {
  const visible = manifests.filter((m) => m.shipped || opts.includeUnshipped);
  const byGroup = new Map<string, { label: string; order: number; modules: ModuleManifest[] }>();
  for (const m of visible) {
    const g = byGroup.get(m.navGroup.key) ?? { label: m.navGroup.label, order: m.navGroup.order, modules: [] };
    g.modules.push(m);
    byGroup.set(m.navGroup.key, g);
  }
  const groups: NavGroup[] = [...byGroup.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key, g]) => ({
      key,
      label: g.label,
      path: g.modules[0]!.navItems[0]?.path ?? g.modules[0]!.basePath,
      requiresAny: g.modules.map((m) => m.key),
      items: g.modules.flatMap((m) => m.navItems.map((i) => ({ ...i, requires: m.key }))),
    }));
  groups.push(ADMIN_GROUP);

  // Longer prefixes first so "/operations/security" wins over "/operations".
  const routePrefixes = manifests
    .flatMap((m) => [{ prefix: m.basePath, module: m.key }, ...m.navItems.map((i) => ({ prefix: i.path, module: m.key }))])
    .filter((r, i, all) => all.findIndex((x) => x.prefix === r.prefix) === i)
    .sort((a, b) => b.prefix.length - a.prefix.length);

  return { groups, routePrefixes };
}

function startsWithPath(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** The module a path belongs to, or null for shared routes. */
export function moduleForPath(path: string, model: NavModel): ModuleKey | null {
  return model.routePrefixes.find((r) => startsWithPath(path, r.prefix))?.module ?? null;
}

/**
 * The nav an actor sees. A module the tenant is not entitled to is not rendered, and
 * neither is its sub-nav. Under impersonation, and for a client user, Administration
 * collapses to the client-side view and appears only with access to management.
 */
export function visibleNav(actor: Actor, model: NavModel): NavGroup[] {
  const staffSurface = isStaffSurface(actor);
  const persona = personaOf(actor);
  const entitlements = persona.kind === 'tenant' ? persona.tenant.entitlements : null;

  const out: NavGroup[] = [];
  for (const group of model.groups) {
    if (group.key === 'admin') {
      if (staffSurface) {
        out.push(group);
      } else if (can(actor, 'management') !== 'none') {
        const org = group.items.filter((i) => !i.staffOnly);
        const label = actor.kind === 'tenant' ? 'Your organization' : 'Client-side view';
        out.push({ ...group, path: ORG_ROUTE, items: org.map((i) => (i.key === 'org' ? { ...i, label } : i)) });
      }
      continue;
    }

    if (staffSurface) {
      out.push(group);
      continue;
    }

    if (!entitlements) continue;
    const entitled = (group.requiresAny ?? []).some((m) => entitlements[m]);
    if (!entitled) continue;
    const items = group.items.filter((i) => !i.requires || entitlements[i.requires]);
    const path = items[0]?.path ?? group.path;
    out.push({ ...group, path, items });
  }
  return out;
}

export type RouteDecision =
  | { allow: true }
  | { allow: false; redirectTo: string; reason: 'unentitled' | 'staff-only' | 'no-management' };

/**
 * Route gating. Same rules as the nav, so nothing hidden is reachable by URL. Direct URL
 * entry is blocked, not merely hidden.
 */
export function routeDecision(actor: Actor, path: string, model: NavModel): RouteDecision {
  const staffSurface = isStaffSurface(actor);

  if (startsWithPath(path, ADMIN_ROUTE_PREFIX)) {
    return staffSurface ? { allow: true } : { allow: false, redirectTo: ORG_ROUTE, reason: 'staff-only' };
  }

  if (startsWithPath(path, ORG_ROUTE)) {
    if (staffSurface) return { allow: true };
    return can(actor, 'management') !== 'none' ? { allow: true } : { allow: false, redirectTo: HOME_ROUTE, reason: 'no-management' };
  }

  const module = moduleForPath(path, model);
  if (!module || staffSurface) return { allow: true };

  const persona = personaOf(actor);
  const entitled = persona.kind === 'tenant' && persona.tenant.entitlements[module];
  return entitled ? { allow: true } : { allow: false, redirectTo: HOME_ROUTE, reason: 'unentitled' };
}
